import type {
  DirectiveDescriptor,
  MDXEditorMethods,
  MDXEditorProps,
} from '@mdxeditor/editor';
import { forwardRef, useRef, useState } from 'react';
import {
  BlockTypeSelect,
  BoldItalicUnderlineToggles,
  CreateLink,
  diffSourcePlugin,
  DiffSourceToggleWrapper,
  directivesPlugin,
  headingsPlugin,
  imagePlugin,
  InsertImage,
  InsertTable,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  ListsToggle,
  markdownShortcutPlugin,
  MDXEditor,
  quotePlugin,
  Separator,
  tablePlugin,
  thematicBreakPlugin,
  toolbarPlugin,
  UndoRedo,
} from '@mdxeditor/editor';

import { SupportingText } from '~/components/molecules/SupportingText';
import { IContainerProps } from '~/components/utils/Container';
import { MarkdownEditorImageDialog } from './MarkdowEditorImageDialog';

import '@mdxeditor/editor/style.css';

import { Stack } from '@sixui/ui';

import css from './styles.module.css';

export type IMarkdownEditorProps = IContainerProps &
  Omit<MDXEditorProps, 'markdown'> & {
    appId?: string;
    diffMarkdown?: string;
    markdown?: string;
    supportingText?: string;
    hasError?: boolean;
    errorText?: string;
    directivesDescriptors?: Array<DirectiveDescriptor>;
    toolbarContents?: React.ReactNode;
  };

export const MarkdownEditor = forwardRef<
  MDXEditorMethods,
  IMarkdownEditorProps
>(function MarkdownEditor(props, ref?: React.Ref<MDXEditorMethods>) {
  const {
    sx,
    markdown,
    supportingText,
    hasError: hasErrorProp,
    errorText,
    appId,
    diffMarkdown,
    directivesDescriptors,
    toolbarContents,
    ...other
  } = props;

  const [internalErrorText, setInternalErrorText] = useState<
    string | undefined
  >();
  const bottomText = internalErrorText ?? errorText ?? supportingText;
  const hasError = hasErrorProp || !!internalErrorText || !!errorText;
  const sourceModeEnabled = useRef(hasError);

  return (
    <Stack gap={2} sx={sx}>
      <MDXEditor
        ref={ref}
        className={[css.editor, hasError && css.error]
          .filter(Boolean)
          .join(' ')}
        markdown={markdown ?? ''}
        plugins={[
          thematicBreakPlugin(),
          tablePlugin(),
          diffSourcePlugin({
            diffMarkdown,
            viewMode: 'rich-text',
            readOnlyDiff: true,
          }),
          directivesPlugin({
            directiveDescriptors: [...(directivesDescriptors ?? [])],
          }),
          toolbarPlugin({
            toolbarContents: () => (
              <>
                <UndoRedo />
                <Separator />
                <BoldItalicUnderlineToggles />
                <Separator />
                <BlockTypeSelect />
                <Separator />
                <InsertTable />
                <ListsToggle options={['bullet', 'number']} />
                <Separator />
                <CreateLink />
                <Separator />
                <InsertImage />
                {toolbarContents}
                {sourceModeEnabled ? (
                  <DiffSourceToggleWrapper
                    options={
                      diffMarkdown
                        ? ['rich-text', 'diff', 'source']
                        : ['rich-text', 'source']
                    }
                  >
                    <UndoRedo />
                  </DiffSourceToggleWrapper>
                ) : null}
              </>
            ),
          }),
          headingsPlugin(),
          linkPlugin(),
          linkDialogPlugin(),
          listsPlugin(),
          markdownShortcutPlugin(),
          quotePlugin(),
          imagePlugin({
            ImageDialog: () => <MarkdownEditorImageDialog appId={appId} />,
          }),
        ]}
        contentEditableClassName={css.content}
        {...other}
        onError={(...args) => {
          setInternalErrorText(args[0].error);
          other.onError?.(...args);
        }}
        onChange={(...args) => {
          setInternalErrorText(undefined);
          other.onChange?.(...args);
        }}
      />
      {bottomText ? (
        <SupportingText hasError={hasError}>{bottomText}</SupportingText>
      ) : null}
    </Stack>
  );
});
